import { stringify } from "qs";
import { getHeaders, makeRequest, fetch } from "$utils/utils";
import { Shipment, ShippingRecordStatus } from "$types";
import config from "./config";

import { queryClient } from "$utils/queryClient";
import { CompanyServices, OpService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

const { shipping_api_url: shipping_url } = config;

export enum ERRORS {
  UNKOWN_ERROR = "Une erreur inconnue s'est produite.",
}

const getCompany = async (id) => {
  let company = null;
  if (id) {
    company = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANY_BY_ID,
      id,
    ]);

    if (!company) {
      company = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, id],
        queryFn: () => CompanyServices.getCompanyById(id),
        gcTime: 5 * 1000 * 60,
      });
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANY_BY_ID, id],
        company
      );
    }
  }
  return company;
};

const chunkCall = async (array) => {
  const chunkTotal = [];
  for (let i = 0; i < array.length; i += 10) {
    chunkTotal.push(array.slice(i, i + 10));
  }
  const allData = [];

  for await (const chunk of chunkTotal) {
    allData.push(await Promise.all(chunk.map(getCompany)));
  }
  return allData.flat();
};

// TODO move all this to the gateway and add a cache to increase perfs !
export const getShipments = async (params, signal) => {
  try {
    const { data } = (await fetch(
      `/shipments?${stringify(params)}`,
      {},
      shipping_url,
      signal
    )) as any;
    const returnData = await formatShippement(data);
    return returnData;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const getShipmentsWithOpsAndCOmpanies = async (params, signal) => {
  try {
    const { data } = (await fetch(
      `/shipmentWithOpsAndCompanies?${stringify(params)}`,
      {},
      shipping_url,
      signal
    )) as any;
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

const formatShippement = async (shipments) => {
  const opList = [
    ...new Set(
      shipments
        .filter(
          (shipment) =>
            shipment.shippingRecord?.status !== ShippingRecordStatus.DRAFT
        )
        .map((shipment: Shipment) => shipment.opId)
    ),
  ];

  let selectedOps;
  selectedOps = await queryClient.getQueryData([
    CacheInvalidationAction.GET_OPS,
    opList,
  ]);
  if (!selectedOps) {
    const chunkTotal = [];
    const opsTotal = [];
    for (let i = 0; i < opList.length; i += 50) {
      chunkTotal.push(opList.slice(i, i + 50));
    }

    for await (const chunk of chunkTotal) {
      const ops = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_OPS, chunk],

        queryFn: () =>
          OpService.getOps({
            $inId: chunk,
          }),

        gcTime: 5 * 60 * 1000,
      });
      opsTotal.push(ops.data);
    }
    selectedOps = opsTotal.flat();
    await queryClient.setQueryData(
      [CacheInvalidationAction.GET_OPS, opList],
      selectedOps
    );
  }

  const filteredShipments = shipments.filter(
    (shipment) => shipment.shippingRecord?.status !== ShippingRecordStatus.DRAFT
  );

  const agencyListId = [
    ...new Set(
      filteredShipments
        .map((s) => s.shippingRecord.agencyCompanyId)
        .filter(Boolean)
    ),
  ];

  const agencyList = await chunkCall(agencyListId);

  const commissionListId = [
    ...new Set(
      filteredShipments
        .map((s) => s.shippingRecord.commissionCompanyId)
        .filter(Boolean)
    ),
  ];
  const commissionList = await chunkCall(commissionListId);

  const carrierListId = [
    ...new Set(
      filteredShipments
        .map((s) => s.shippingRecord.carrierCompanyId)
        .filter(Boolean)
    ),
  ];
  const carrierList = await chunkCall(carrierListId);

  const data = filteredShipments.map((shipment: Shipment) => ({
    ...shipment,
    op: selectedOps.find((op) => op._id === shipment.opId),
    shippingRecord: {
      ...shipment.shippingRecord,
      carrierCompany: carrierList.find(
        (c) => c._id === shipment.shippingRecord.carrierCompanyId
      ),
      agencyCompany: agencyList.find(
        (c) => c._id === shipment.shippingRecord.agencyCompanyId
      ),
      commissionCompany: commissionList.find(
        (c) => c._id === shipment.shippingRecord.commissionCompanyId
      ),
    },
    disabled: shipment.shippingRecord?.status === ShippingRecordStatus.CANCELED,
  }));

  return data;
};

/**
 * Fetches a shipment record that includes the specified operation
 * If the operation is part of an already shipped shipment, this is indicated in the response
 */
export const GetOpInAShippedShipment = async (
  token: string,
  opId: string
): Promise<Shipment | null> => {
  try {
    // Check if we already have this data from the aggregated response
    // This would be implemented in the frontend component

    const res = await makeRequest(
      `${shipping_url}/shipments/opId/${opId}`,
      "GET",
      undefined,
      getHeaders(token)
    );

    if (!res) {
      throw new Error(ERRORS.UNKOWN_ERROR);
    }

    return res.data;
  } catch (error: any) {
    // If it's a 400 Bad Request, it means the shipment is already being processed
    if (error.response && error.response.status === 400) {
      // Return the error response data to indicate a shipped shipment
      return error.response.data;
    }

    // For other errors, return null
    console.error(error);
    return null;
  }
};

export const GetIsOneOpInAShippedShipment = async (
  token: string,
  opIds: string[]
): Promise<boolean> => {
  if (!opIds || opIds?.length === 0) return false;
  const res = await makeRequest(
    `${shipping_url}/shipments?opIds=${opIds.join(",")}`,
    "HEAD",
    undefined,
    getHeaders(token)
  );
  return res.status === 204;
};
